import { useDispatch } from "react-redux";

import { useQueryClient } from "@tanstack/react-query";

import { apiThatThrows } from "containers/Request";
import { FilterSuggestion } from "hooks/tivoApps";
import {
  TiVoDeviceClientType,
  TiVoDeviceOem,
  TiVoDevicePlatform,
} from "hooks/tivoDevices";
import { ApiResponse } from "types/api";

const STALE_TIME = 300000;

const createSuggestion = (name: string): FilterSuggestion => ({
  icon: null,
  text: name,
  filter_value: name,
});

const uniqueSuggestions = (suggestions: FilterSuggestion[]) => [
  ...new Map(
    suggestions.map((suggestion) => [suggestion.text, suggestion])
  ).values(),
];

export const useManifestFiltersSuggestions = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const fetchSuggestions = async (params: {
    for_filter: string;
    text: string;
  }) => {
    return queryClient.fetchQuery({
      queryKey: ["manifestSuggestions", params.for_filter],
      queryFn: async () => {
        // @todo TAP-633: Replace mock implementation with actual suggestions API endpoint
        const data: ApiResponse<TiVoDevicePlatform[]> = await dispatch(
          apiThatThrows.getTiVoPlatforms.action({
            queryParams: {
              include: ["oems", "oems.clientTypes"],
            },
          })
        );

        let results: FilterSuggestion[] = [];

        if (params.for_filter === "platform") {
          results = data.results.map((platform: TiVoDevicePlatform) =>
            createSuggestion(platform.name)
          );
        } else if (params.for_filter === "oem") {
          results = data.results
            .flatMap((platform) => platform.oems ?? [])
            .map((oem: TiVoDeviceOem) => createSuggestion(oem.name));
        } else if (params.for_filter === "client_type") {
          results = data.results
            .flatMap((platform) => platform.oems ?? [])
            .flatMap((oem) => oem.clientTypes ?? [])
            .map((clientType: TiVoDeviceClientType) =>
              createSuggestion(clientType.name)
            );
        }

        return {
          results: uniqueSuggestions(results),
        };
      },
      staleTime: STALE_TIME,
    });
  };

  return { fetchSuggestions };
};
